import {
  AccountNotifications,
  AlertInbox,
  AlertList,
  AlertRuleList,
  AlertSilences,
  ProjectAlertChannels,
  TelegramBot,
  TelegramLink,
  TestTelegramBotResponse,
  type AlertKind,
  type AlertSeverity,
  type CreateMaintenanceRequest,
  type NotificationPreferences,
  type SetAlertRuleRequest,
} from "@cloudserver/contracts";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

/*
 * Alerts (docs/alerts.md): the caller's notifications and Telegram links,
 * the organisation's bots and default rules, a project's alerts, rules,
 * silences and chats. Project keys sit under `projects`, so project events
 * (and `alert.changed`) refresh them; the caller's own under `account`.
 */

export const alertKeys = {
  account: ["account", "notifications"] as const,
  accountAlerts: ["account", "alerts"] as const,
  inbox: ["account", "alerts", "inbox"] as const,
  bots: ["alerts", "bots"] as const,
  orgRules: ["alerts", "rules"] as const,
  project: (projectId: string) => ["projects", projectId, "alerts"] as const,
};

const FIRING_REFRESH_MS = 30_000;
/** While a link dialog is open. */
const WATCH_MS = 3_000;
const PAGE = 50;

type ListFilter = { status: "firing" | "all"; serverId?: string | undefined };

const query = (filter: ListFilter, before?: string) => {
  const params = new URLSearchParams({ status: filter.status, limit: String(PAGE) });
  if (filter.serverId) params.set("serverId", filter.serverId);
  if (before) params.set("before", before);
  return params.toString();
};

/** A list of alerts, a page at a time (`fetchNextPage` for older ones). */
function useAlertPages(key: QueryKey, base: string, filter: ListFilter) {
  return useInfiniteQuery({
    queryKey: [...key, "list", filter],
    queryFn: ({ pageParam }) =>
      api(`${base}?${query(filter, pageParam ?? undefined)}`, { schema: AlertList }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    refetchInterval: filter.status === "firing" ? FIRING_REFRESH_MS : false,
  });
}

/** Alerts of every project the caller can see. */
export const useAccountAlerts = (filter: ListFilter) =>
  useAlertPages(alertKeys.accountAlerts, "/account/alerts", filter);

export const useProjectAlerts = (projectId: string, filter: ListFilter) =>
  useAlertPages(alertKeys.project(projectId), `/projects/${projectId}/alerts`, filter);

function useInvalidating<T, R>(keys: QueryKey[], fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      for (const queryKey of keys) await client.invalidateQueries({ queryKey });
    },
  });
}

// --- The caller's own --------------------------------------------------------

/** `watching`: a link dialog is open, so look again soon for the chat the bot just got. */
export const useAccountNotifications = (watching = false) =>
  useQuery({
    queryKey: alertKeys.account,
    queryFn: () => api("/account/notifications", { schema: AccountNotifications }),
    refetchInterval: watching ? WATCH_MS : false,
  });

export const useSavePreferences = () =>
  useInvalidating([alertKeys.account], (preferences: NotificationPreferences) =>
    api("/account/notifications", {
      method: "PUT",
      body: preferences,
      schema: AccountNotifications,
    }),
  );

export const useLinkTelegram = () =>
  useMutation({
    mutationFn: (botId: string) =>
      api("/account/notifications/telegram", {
        method: "POST",
        body: { botId },
        schema: TelegramLink,
      }),
  });

export const useUnlinkTelegram = () =>
  useInvalidating([alertKeys.account], (chatId: string) =>
    api(`/account/notifications/telegram/${chatId}`, { method: "DELETE" }),
  );

export const useInbox = (enabled = true) =>
  useQuery({
    queryKey: alertKeys.inbox,
    queryFn: () => api("/account/alerts/inbox", { schema: AlertInbox }),
    enabled,
    refetchInterval: 60_000,
  });

export const useMarkInboxRead = () =>
  useInvalidating([alertKeys.inbox], (ids?: string[]) =>
    api("/account/alerts/inbox/read", { method: "POST", body: ids ? { ids } : {} }),
  );

// --- Bots and organisation defaults (administrators) ---------------------------

export const useBots = (enabled = true) =>
  useQuery({
    queryKey: alertKeys.bots,
    queryFn: () => api("/alerts/bots", { schema: z.array(TelegramBot) }),
    enabled,
  });

export const useAddBot = () =>
  useInvalidating([alertKeys.bots, alertKeys.account], (token: string) =>
    api("/alerts/bots", { method: "POST", body: { token }, schema: TelegramBot }),
  );

export const useMakeDefaultBot = () =>
  useInvalidating([alertKeys.bots, alertKeys.account, ["projects"]], (botId: string) =>
    api(`/alerts/bots/${botId}`, {
      method: "PATCH",
      body: { isDefault: true },
      schema: z.array(TelegramBot),
    }),
  );

export const useRemoveBot = () =>
  useInvalidating([alertKeys.bots, alertKeys.account, ["projects"]], (botId: string) =>
    api(`/alerts/bots/${botId}`, { method: "DELETE" }),
  );

export const useTestBot = () =>
  useMutation({
    mutationFn: (input: { botId: string; targetId?: string | null }) =>
      api(`/alerts/bots/${input.botId}/test`, {
        method: "POST",
        body: { targetId: input.targetId ?? null },
        schema: TestTelegramBotResponse,
      }),
  });

export const useOrgRules = (enabled = true) =>
  useQuery({
    queryKey: alertKeys.orgRules,
    queryFn: () => api("/alerts/rules", { schema: AlertRuleList }),
    enabled,
  });

/** Where a rule editor writes: organisation defaults, a project, or one server. */
export interface RuleScope {
  projectId?: string | undefined;
  serverId?: string | undefined;
}

const rulesPath = (scope: RuleScope) =>
  scope.projectId
    ? scope.serverId
      ? `/projects/${scope.projectId}/servers/${scope.serverId}/alerts/rules`
      : `/projects/${scope.projectId}/alerts/rules`
    : "/alerts/rules";

const rulesKey = (scope: RuleScope): QueryKey =>
  scope.projectId
    ? [...alertKeys.project(scope.projectId), "rules", scope.serverId ?? "project"]
    : alertKeys.orgRules;

export const useRules = (scope: RuleScope, enabled = true) =>
  useQuery({
    queryKey: rulesKey(scope),
    queryFn: () => api(rulesPath(scope), { schema: AlertRuleList }),
    enabled,
  });

export function useSetRule(scope: RuleScope) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: AlertKind; rule: SetAlertRuleRequest | null }) =>
      api(`${rulesPath(scope)}/${input.kind}`, {
        method: input.rule ? "PUT" : "DELETE",
        ...(input.rule ? { body: input.rule } : {}),
        schema: AlertRuleList,
      }),
    onSuccess: (answer) => {
      client.setQueryData(rulesKey(scope), answer);
      // A level below may inherit the change.
      void client.invalidateQueries({
        queryKey: scope.projectId ? alertKeys.project(scope.projectId) : ["projects"],
      });
    },
  });
}

// --- A project's alerts ---------------------------------------------------------

export const useMuteAlert = (projectId: string) =>
  useInvalidating(
    [alertKeys.project(projectId), alertKeys.accountAlerts],
    (input: { alertId: string; minutes?: number }) =>
      api(`/projects/${projectId}/alerts/${input.alertId}/mute`, {
        method: "POST",
        body: { minutes: input.minutes ?? 60 },
        schema: z.object({ until: z.iso.datetime() }),
      }),
  );

export const useMuteServer = (projectId: string, serverId: string) =>
  useInvalidating([alertKeys.project(projectId)], (minutes: number) =>
    api(`/projects/${projectId}/servers/${serverId}/alerts/mute`, {
      method: "POST",
      body: { minutes },
      schema: z.object({ until: z.iso.datetime() }),
    }),
  );

export const useSilences = (projectId: string) =>
  useQuery({
    queryKey: [...alertKeys.project(projectId), "silences"],
    queryFn: () => api(`/projects/${projectId}/alerts/silences`, { schema: AlertSilences }),
  });

export const useUnmute = (projectId: string) =>
  useInvalidating([alertKeys.project(projectId)], (muteId: string) =>
    api(`/projects/${projectId}/alerts/mutes/${muteId}`, { method: "DELETE" }),
  );

export const useCreateMaintenance = (projectId: string) =>
  useInvalidating([alertKeys.project(projectId)], (window: CreateMaintenanceRequest) =>
    api(`/projects/${projectId}/alerts/maintenance`, {
      method: "POST",
      body: window,
      schema: AlertSilences,
    }),
  );

export const useDeleteMaintenance = (projectId: string) =>
  useInvalidating([alertKeys.project(projectId)], (windowId: string) =>
    api(`/projects/${projectId}/alerts/maintenance/${windowId}`, { method: "DELETE" }),
  );

export const useProjectChannels = (projectId: string, watching = false) =>
  useQuery({
    queryKey: [...alertKeys.project(projectId), "channels"],
    queryFn: () => api(`/projects/${projectId}/alerts/channels`, { schema: ProjectAlertChannels }),
    refetchInterval: watching ? WATCH_MS : false,
  });

export const useSetProjectBot = (projectId: string) =>
  useInvalidating([alertKeys.project(projectId), alertKeys.bots], (botId: string | null) =>
    api(`/projects/${projectId}/alerts/channels`, {
      method: "PUT",
      body: { botId },
      schema: ProjectAlertChannels,
    }),
  );

export const useLinkGroup = (projectId: string) =>
  useMutation({
    mutationFn: (minSeverity: AlertSeverity) =>
      api(`/projects/${projectId}/alerts/channels/groups`, {
        method: "POST",
        body: { minSeverity },
        schema: TelegramLink,
      }),
  });

export const useRemoveGroup = (projectId: string) =>
  useInvalidating([alertKeys.project(projectId)], (targetId: string) =>
    api(`/projects/${projectId}/alerts/channels/groups/${targetId}`, { method: "DELETE" }),
  );
